import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { BRAND, ACTIVE_LOCATIONS, QUICK_LINKS } from "../lib/constants";
import NewsletterForm from "./NewsletterForm";

export const Footer = () => {
  return (
    <footer className="bg-ink text-white relative" data-testid="site-footer">
      <div className="grain absolute inset-0 opacity-60 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-10">
        {/* Newsletter band */}
        <div className="border border-white/10 p-8 sm:p-12 mb-20" id="newsletter">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">DWC · List</div>
              <h3 className="font-serif text-4xl sm:text-5xl leading-[0.95] tracking-tight">
                Be the first to receive <br/><em className="text-brand">new drops, deals</em> & invites.
              </h3>
            </div>
            <div>
              <p className="text-white/70 text-base mb-6 max-w-md leading-relaxed">
                Early access to limited batches, members-only pricing, and updates on your medical registration.
                No spam — unsubscribe anytime.
              </p>
              <div className="bg-white">
                <NewsletterForm source="footer" testid="footer-newsletter" />
              </div>
            </div>
          </div>
        </div>

        {/* Main footer grid */}
        <div className="grid gap-10 md:grid-cols-12 pb-14">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="DWC" className="h-14 w-14 bg-white p-1" />
              <div>
                <div className="font-serif text-xl">District Wellness Center</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Licensed DC · Georgia Ave</div>
              </div>
            </div>
            <p className="mt-6 text-white/70 text-base max-w-sm leading-relaxed">
              A trusted medical cannabis retailer serving Washington, DC from our Georgia Ave flagship.
            </p>
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-brand" data-testid="footer-email">
                <Mail size={14} /> {BRAND.email}
              </a>
              <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-brand" data-testid="footer-phone">
                <Phone size={14} /> {BRAND.phone}
              </a>
              <a
                href={BRAND.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-brand"
                aria-label="District Wellness Center on Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-5">Quick Links</div>
            <ul className="space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/80 hover:text-brand link-underline" data-testid={`footer-link-${l.to.replace('/','') || 'home'}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-5">Visit Our Dispensary</div>
            <ul className={`grid gap-5 ${ACTIVE_LOCATIONS.length === 1 ? "sm:grid-cols-1" : "sm:grid-cols-3"}`}>
              {ACTIVE_LOCATIONS.map((loc) => (
                <li key={loc.id} className="border-l border-white/15 pl-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-brand mb-2">{loc.label}</div>
                  <div className="font-serif text-lg leading-tight">{loc.street}</div>
                  <div className="text-sm text-white/60">{loc.city}</div>
                  <div className="text-[11px] text-white/40 mt-2">{loc.hours}</div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.mapQuery)}`}
                    target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-brand"
                    data-testid={`footer-directions-${loc.id}`}
                  >
                    Directions <ArrowUpRight size={12} />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Licensed By</div>
              <div className="text-sm text-white/70">DC Alcoholic Beverage &amp; Cannabis Administration</div>
              <div className="text-[11px] text-white/50 mt-1">License {BRAND.licenseNumber} · {BRAND.legalName} (T/A {BRAND.dba})</div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] text-white/40">
          <div className="flex items-center gap-2">
            <MapPin size={12} className="text-brand" />
            Licensed Medical Cannabis Retailer · DC ABCA License {BRAND.licenseNumber}
          </div>
          <div>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
